import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import Button from '../../../components/common/Button/Button';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../../components/common/DecoratedHeader/DecoratedHeader';
import Loading from '../../../components/common/Loading/Loading';
import Logo from '../../../components/common/Logo/Logo';
import config from '../../../config/environment';
import {
  AnamnesisService,
  PublicStudentContextService,
} from '../../../services';
import { STUDENT_TOKEN_QUERY_PARAM } from '../../../utils/studentPublicLinks';
import PublicAnamneseForm from './PublicAnamneseForm';
import PublicFormAnsweredState from '../../../components/common/PublicFormAnsweredState/PublicFormAnsweredState';
import {
  buildPublicAnamnesePayload,
  createEmptyPublicAnamneseLockedFields,
  createInitialPublicAnamneseForm,
  mapStudentDataToPublicAnamneseForm,
  maskPublicFieldValue,
  resolvePublicAnamneseLockedFields,
  sanitizePublicAnamneseForm,
  validatePublicAnamneseForm,
  focusNearestPublicAnamneseError,
} from './publicAnamnese.utils';
import type { PublicAnamneseFormState, PublicAnamneseLockedFields } from './publicAnamnese.types';
import styles from '../../Fup/Fup.module.css';

type FeedbackState = {
  message: string;
  variant: 'error' | 'success';
} | null;

const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
};

const scrollToPageTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const AnamnesisRegister: React.FC = () => {
  const [searchParams] = useSearchParams();
  const studentToken = searchParams.get(STUDENT_TOKEN_QUERY_PARAM)?.trim() || '';
  const [formData, setFormData] = useState<PublicAnamneseFormState>(createInitialPublicAnamneseForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [isLoadingStudent, setIsLoadingStudent] = useState(Boolean(studentToken));
  const [loadStudentError, setLoadStudentError] = useState<string | null>(null);
  const [lockedFields, setLockedFields] = useState<PublicAnamneseLockedFields>(
    createEmptyPublicAnamneseLockedFields,
  );
  const [studentId, setStudentId] = useState<number | null>(null);
  const [isAlreadyAnswered, setIsAlreadyAnswered] = useState(false);
  const [answeredAt, setAnsweredAt] = useState<string | null>(null);
  const lastLookedUpCep = useRef('');

  const handleFieldChange = useCallback(<K extends keyof PublicAnamneseFormState>(
    field: K,
    value: PublicAnamneseFormState[K],
  ) => {
    setFormData((prev) => {
      const nextValue = typeof value === 'string'
        ? maskPublicFieldValue(field, value, prev.callingCode, prev.country) as PublicAnamneseFormState[K]
        : value;

      return sanitizePublicAnamneseForm({
        ...prev,
        [field]: nextValue,
      });
    });
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setFeedback(null);
  }, []);

  useEffect(() => {
    if (!studentToken) {
      setIsLoadingStudent(false);
      setLoadStudentError('Este formulário só pode ser acessado pelo link enviado. Solicite um novo link à equipe.');
      setLockedFields(createEmptyPublicAnamneseLockedFields());
      setStudentId(null);
      setIsAlreadyAnswered(false);
      setAnsweredAt(null);
      return;
    }

    let isMounted = true;

    const loadStudentData = async () => {
      setIsLoadingStudent(true);
      setLoadStudentError(null);

      try {
        const context = await PublicStudentContextService.resolve(studentToken);

        if (!isMounted) {
          return;
        }

        setFormData(mapStudentDataToPublicAnamneseForm(
          context.student,
          context.phone,
          context.address,
          null,
        ));
        setLockedFields(resolvePublicAnamneseLockedFields(context.student, context.phone));
        setStudentId(context.student.id);
        setIsAlreadyAnswered(!context.formsStatus.can_submit_anamnesis);
        setAnsweredAt(context.formsStatus.last_complete_anamnesis_at);
        setErrors({});
        setFeedback(null);
        lastLookedUpCep.current = '';
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setLoadStudentError(
          error instanceof Error ? error.message : 'Não foi possível carregar os dados da aluna.',
        );
      } finally {
        if (isMounted) {
          setIsLoadingStudent(false);
        }
      }
    };

    void loadStudentData();

    return () => {
      isMounted = false;
    };
  }, [studentToken]);

  const lookupCep = useCallback(async () => {
    const cep = formData.cep.replace(/\s/g, '');
    if (!cep || cep.length < 3) {
      return;
    }

    const countryCode = formData.country || 'BR';
    if (countryCode === 'BR' && cep.replace(/\D/g, '').length !== 8) {
      return;
    }

    const key = `${countryCode}:${cep}`;
    if (lastLookedUpCep.current === key) {
      return;
    }

    lastLookedUpCep.current = key;
    setLoadingCep(true);

    try {
      const response = await fetch(`${config.supabaseFunctionsUrl}/address-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zipCode: cep,
          countryCode,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.error) {
        setErrors((prev) => ({ ...prev, cep: data?.error || 'CEP não encontrado.' }));
        return;
      }

      setErrors((prev) => ({ ...prev, cep: '' }));
      setFormData((prev) => sanitizePublicAnamneseForm({
        ...prev,
        street: data.street || prev.street,
        complement: data.complement || prev.complement,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: data.state || prev.state,
      }));
    } catch {
      lastLookedUpCep.current = '';
      setErrors((prev) => ({ ...prev, cep: 'Erro ao buscar CEP.' }));
    } finally {
      setLoadingCep(false);
    }
  }, [formData.cep, formData.country]);

  const handleOpenConfirmModal = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!studentToken) {
      setFeedback({
        message: 'Este formulário só pode ser acessado pelo link enviado. Solicite um novo link à equipe.',
        variant: 'error',
      });
      return;
    }

    if (isAlreadyAnswered) {
      return;
    }

    const nextErrors = validatePublicAnamneseForm(formData, { minimalPersonalData: true });
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      window.setTimeout(() => {
        focusNearestPublicAnamneseError(nextErrors);
      }, 0);
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!studentToken) {
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await AnamnesisService.submitRegistration(buildPublicAnamnesePayload(formData, {
        studentId: studentId ?? undefined,
        studentToken,
      }));
      setShowConfirmModal(false);
      setIsAlreadyAnswered(true);
      setAnsweredAt(new Date().toISOString());
      setFeedback(null);
      scrollToPageTop();
    } catch (error: unknown) {
      setFeedback({
        message: getErrorMessage(error, 'Não foi possível enviar a anamnese agora.'),
        variant: 'error',
      });
      scrollToPageTop();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingStudent) {
    return <Loading fullscreen />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.header}>
          <Logo />
        </div>

        <section className={styles.panelCard}>
          <DecoratedHeader title="Anamnese e consultoria de Treinamento" />

          {loadStudentError && (
            <AlertBanner
              message={loadStudentError}
              variant="error"
              className={styles.feedbackAlert}
            />
          )}

          {feedback && (
            <AlertBanner
              message={feedback.message}
              variant={feedback.variant}
              className={styles.feedbackAlert}
            />
          )}

          {!loadStudentError && isAlreadyAnswered && (
            <PublicFormAnsweredState
              title="Sua anamnese já foi respondida."
              thankYouMessage="Obrigada por compartilhar suas informações conosco. Nossa equipe já recebeu seus dados."
              answeredAt={answeredAt}
            />
          )}

          {!loadStudentError && !isAlreadyAnswered && studentToken && (
          <form className={styles.formWrapper} onSubmit={handleOpenConfirmModal} noValidate>
            <PublicAnamneseForm
              data={formData}
              errors={errors}
              disabled={isSubmitting}
              lockedFields={lockedFields}
              loadingCep={loadingCep}
              minimalPersonalData
              onFieldChange={handleFieldChange}
              onCepBlur={lookupCep}
            />

            <div className={styles.footerActions}>
              <Button
                type="submit"
                variant="primary"
                size="app"
                fullWidth
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar anamnese'}
              </Button>
            </div>
          </form>
          )}
        </section>
      </div>

      <ConfirmModal
        open={showConfirmModal}
        title="Confirmar envio"
        confirmLabel="Confirmar envio"
        cancelLabel="Cancelar"
        variant="warning"
        loading={isSubmitting}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSubmit}
      >
        <p>Após enviar sua anamnese, não será possível alterá-la posteriormente.</p>
        <p>Deseja realmente enviar sua anamnese?</p>
      </ConfirmModal>
    </div>
  );
};

export default AnamnesisRegister;
